import React, { useEffect, useMemo } from 'react';
import {Form, Formik} from 'formik';
import * as Yup from 'yup';
import {useIntl} from 'react-intl';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../../../../hooks/hooks';
import {Employee} from '../../../../features/employees/types';
import {addEmployee, getEmployees} from '../../../../features/employees';
import {getRoles} from '../../../../features/roles/filter';
import {getProjects} from '../../../../features/projects/filter';
import {getWorkCalendars} from '../../../../features/settings/workcalendar';
import {NAVIGATION_ROUTES} from '../../../../routes/NavigationRoute';
import BaseForm from '../../../../components/shared/form-controls/base-form';
import {FormInput} from '../../../../components/shared/form-controls/form-input';
import {FormCheckbox} from '../../../../components/shared/form-controls/form-checkbox';
import FormAutocomplete from '../../../../components/shared/form-controls/form-autocomplete';
import {AutoCompleteItemValue} from '../../../../components/shared/form-controls/form-autocomplete/types';
import {PIdNameModel} from '../../../../features/projects/types';
import {phoneRegex} from '../../../../features/employees/consts';
import {Lx} from '../../../../i18n/consts';
import {DropdownField} from '../../../../components/shared/nextgen/dropdown-field';
import styles from '../../employees.module.scss';
import {
    canManageEmployeeAdminLabel,
    canManageEmployeeProjects,
    canManageEmployeeRoles,
    canManageEmployeeWorkCalendar,
    canManageEmployees,
} from '../../../../features/profile/entityPermissions';

interface CreateFormValues {
    firstName: string;
    lastName: string;
    email?: string;
    phoneNumber?: string;
    position?: string;
    workCalendar?: PIdNameModel | null;
    isActive: boolean;
    admin: boolean;
    roles: PIdNameModel[];
    projects: PIdNameModel[];
}

export default function CreateEmployeeForm() {
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const navigate = useNavigate();
    const profile = useAppSelector(store => store.profile);
    const roles = useAppSelector(store => store.eRoles);
    const projects = useAppSelector(store => store.eProjects);
    const workCalendars = useAppSelector(store => store.workCalendar.workCalendars);
    const employeesState = useAppSelector(store => store.employees);
    const canEditEmployees = canManageEmployees(profile);
    const canEditEmployeeProjects = canManageEmployeeProjects(profile);
    const canEditEmployeeRoles = canManageEmployeeRoles(profile);
    const canEditEmployeeAdminLabel = canManageEmployeeAdminLabel(profile, employeesState.canEditAdminLabel);
    const canEditEmployeeWorkCalendar = canManageEmployeeWorkCalendar(profile);

    const availableWorkCalendars = useMemo<PIdNameModel[]>(
        () => workCalendars.map((calendar) => ({
            id: calendar.id,
            name: calendar.name,
            isDefault: calendar.isRoot,
        })),
        [workCalendars]
    );
    const rootCalendar = availableWorkCalendars.find((calendar) => calendar.isDefault) ?? availableWorkCalendars[0];

    useEffect(() => {
        if (!canEditEmployees) {
            navigate(NAVIGATION_ROUTES.EMPLOYEES);
            return;
        }

        if (canEditEmployeeRoles && roles.length === 0) {
            dispatch(getRoles());
        }

        if (canEditEmployeeProjects && projects.length === 0) {
            dispatch(getProjects());
        }

        if (canEditEmployeeWorkCalendar && workCalendars.length === 0) {
            dispatch(getWorkCalendars());
        }

        if (employeesState.items.length === 0) {
            dispatch(getEmployees(0));
        }
    }, [
        canEditEmployeeProjects,
        canEditEmployeeRoles,
        canEditEmployees,
        canEditEmployeeWorkCalendar,
        dispatch,
        employeesState.items.length,
        navigate,
        projects.length,
        roles.length,
        workCalendars.length,
    ]);

    if (!canEditEmployees) {
        return null;
    }

    const initialValues: CreateFormValues = {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        position: '',
        workCalendar: rootCalendar,
        isActive: true,
        admin: false,
        roles: [],
        projects: [],
    };

    const validationSchema: Yup.Schema<CreateFormValues> = Yup.object({
        firstName: Yup.string()
            .max(45, intl.formatMessage({ id: Lx.Form.MAX_LENGTH_ERROR }))
            .required(`${intl.formatMessage({ id: Lx.Employee.FIRST_NAME })} ${intl.formatMessage({ id: Lx.Form.REQUIRED })}`),
        lastName: Yup.string()
            .max(45, intl.formatMessage({ id: Lx.Form.MAX_LENGTH_ERROR }))
            .required(`${intl.formatMessage({ id: Lx.Employee.LAST_NAME })} ${intl.formatMessage({ id: Lx.Form.REQUIRED })}`),
        email: Yup.string().email().optional(),
        phoneNumber: Yup.string().matches(phoneRegex, intl.formatMessage({ id: Lx.Form.PHONE_NUMBER_ISNT_VALID })).optional(),
        position: Yup.string().optional(),
        workCalendar: Yup.mixed<PIdNameModel>().nullable().optional(),
        isActive: Yup.boolean().required(),
        admin: Yup.boolean().required(),
        roles: Yup.array().of(Yup.object().shape({
            id: Yup.number().required(),
            name: Yup.string().required(),
            isDefault: Yup.boolean().required(),
        })).required().min(0),
        projects: Yup.array().of(Yup.object().shape({
            id: Yup.number().required(),
            name: Yup.string().required(),
            isDefault: Yup.boolean().required(),
        })).required().min(0),
    });

    const save = async (values: CreateFormValues) => {
        const payload: Employee = {
            id: 0,
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email ?? '',
            admin: values.admin,
            phoneNumber: values.phoneNumber ?? '',
            position: values.position ?? '',
            isActive: values.isActive,
            workCalendar: values.workCalendar ?? undefined,
            projects: values.projects,
            roles: values.roles,
            canEditAdminLabel: canEditEmployeeAdminLabel,
            canEditProjects: canEditEmployeeProjects,
            canEditRoles: canEditEmployeeRoles,
        };

        await dispatch(addEmployee(payload)).unwrap();
        await dispatch(getEmployees(0)).unwrap();
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            validateOnBlur={true}
            validateOnChange={true}
            onSubmit={save}>
            <Form>
                <BaseForm
                    contentGap={24}
                    formType='create'
                    formHeader={Lx.Employee.CREATE_EMPLOYEE}
                    savingTitle='Employee'
                    onClose={() => navigate(NAVIGATION_ROUTES.EMPLOYEES)}
                    onAfterSave={() => navigate(NAVIGATION_ROUTES.EMPLOYEES)}>
                    <div className={styles.nameInputsRow}>
                        <div className={styles.nameFieldWrapper}>
                            <FormInput
                                name='firstName'
                                label={intl.formatMessage({ id: Lx.Employee.FIRST_NAME })}
                                placeholder={intl.formatMessage({ id: Lx.Employee.FIRST_NAME })}
                                limited
                                required={true}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div className={styles.nameFieldWrapper}>
                            <FormInput
                                name='lastName'
                                label={intl.formatMessage({ id: Lx.Employee.LAST_NAME })}
                                placeholder={intl.formatMessage({ id: Lx.Employee.LAST_NAME })}
                                limited
                                required={true}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>
                    <FormInput
                        name='email'
                        label={intl.formatMessage({ id: Lx.General.EMAIL })}
                        placeholder={intl.formatMessage({ id: Lx.General.EMAIL })}
                        style={{ width: '100%', maxWidth: '504px' }}
                    />
                    <FormInput
                        name='phoneNumber'
                        label={intl.formatMessage({ id: Lx.General.PHONENUMBER })}
                        placeholder={intl.formatMessage({ id: Lx.General.PHONENUMBER })}
                        style={{ width: '100%', maxWidth: '504px' }}
                    />
                    <FormInput
                        name='position'
                        label={intl.formatMessage({ id: Lx.Employee.POSITION })}
                        placeholder={intl.formatMessage({ id: Lx.Employee.POSITION })}
                        style={{ width: '100%', maxWidth: '504px' }}
                    />
                    <div style={{ width: '100%', maxWidth: '504px' }}>
                        {canEditEmployeeWorkCalendar && (
                            <DropdownField
                                required={false}
                                label={intl.formatMessage({ id: Lx.General.WORK_CALENDAR })}
                                name='workCalendar'
                                items={availableWorkCalendars}
                                placeholder={intl.formatMessage({ id: Lx.General.WORK_CALENDAR })}
                            />
                        )}
                    </div>
                    {canEditEmployeeProjects && (
                        <FormAutocomplete
                            name='projects'
                            label={intl.formatMessage({ id: Lx.General.PROJECTS })}
                            placeHolder={intl.formatMessage({ id: Lx.General.START_TYPING })}
                            items={projects as AutoCompleteItemValue[]}
                        />
                    )}
                    {canEditEmployeeRoles && (
                        <FormAutocomplete
                            name='roles'
                            label={intl.formatMessage({ id: Lx.General.ROLES })}
                            placeHolder={intl.formatMessage({ id: Lx.General.START_TYPING })}
                            items={roles as AutoCompleteItemValue[]}
                        />
                    )}
                    {canEditEmployeeAdminLabel && (
                        <FormCheckbox
                            name='admin'
                            label={intl.formatMessage({ id: Lx.Employee.ADMIN })}
                            style={{ marginBottom: 0 }}
                        />
                    )}
                    <FormCheckbox
                        name='isActive'
                        label={intl.formatMessage({ id: 'active' })}
                        style={{ marginBottom: 0 }}
                    />
                </BaseForm>
            </Form>
        </Formik>
    );
}
